// ============================================================================
// Reservation Service
// Fetches reservation data from the admin-backend API
// ============================================================================

import { fetchWithAuth } from './auth';
import {
  Reservation,
  ReservationStatus,
  PaymentStatus,
  ReservationRvInfo,
  ReservationBookedRv,
  ReservationBookedPv,
} from '../types';
import { addDays, getLocalDateTimeString, parseBackendUtc } from '../utils/dateUtils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8004/api';

// ============================================================================
// API Response Types (match backend Pydantic schemas)
// ============================================================================

export interface ApiCustomer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  main_phone: string;
}

export interface ApiSiteRef {
  id: number;
  alias: string;
  store_id?: number;
}

export interface ApiReservationSite {
  reservation_id: number;
  site_id: number;
  site_cost: number;
  adults: number;
  children: number;
  pets: number;
  vehicles: number;
  status: string;
  cancelled_at?: string;
  cancellation_reason?: string;
  added_after_booking?: boolean;
  // Booking-time vehicle data. rv is a single object; pv is an array.
  // Backend stores all rv values as strings (see ReservationBookedRv).
  rv?: ReservationBookedRv | null;
  pv?: ReservationBookedPv[] | null;
  site?: ApiSiteRef;
}

export interface ApiReservation {
  id: number;
  store_id: number;
  customer_id?: number;
  member_id?: number;
  check_in: string;
  days: number;
  adult: number;
  child: number;
  pet: number;
  vehicle: number;
  cost: number;
  status: string;
  confirmation_code?: string;
  emergency_name?: string;
  emergency_phone?: string;
  emergency_relation?: string;
  special_requests?: string;
  internal_notes?: string;
  guest_first_name?: string;
  guest_last_name?: string;
  other_guests?: string;
  total_sites?: number;
  active?: boolean;
  is_current_version?: boolean;
  reservation_version?: number;
  original_reservation_id?: number;
  supersedes_reservation_id?: number;
  superseded_by_reservation_id?: number;
  created_at?: string;
  updated_at?: string;
  customer?: ApiCustomer;
  reservation_sites?: ApiReservationSite[];
}

export interface ReservationModificationLog {
  id: number;
  original_reservation_id: number;
  old_reservation_id: number;
  new_reservation_id: number;
  modification_type: string;
  change_summary: any;
  cost_difference: number;
  refund_amount?: number;
  additional_charge?: number;
  payment_transaction_id?: string;
  refund_transaction_id?: string;
  modified_by?: number;
  modified_at?: string;
  notes?: string;
  created_at?: string;
}

// Types for batch reservation history endpoint
export interface ReservationVersionSite {
  alias: string;
  site_cost: number;
  site_id?: number;
  adults: number;
  children: number;
  pets: number;
  vehicles: number;
}

export interface ReservationVersion {
  id: number;
  version: number;
  status: string;
  is_current_version: boolean;
  active: boolean;
  check_in: string | null;
  check_out: string | null;
  days: number;
  cost: number;
  sites: ReservationVersionSite[];
  groupevent_booking_code?: string | null;
  supersedes_reservation_id: number | null;
  superseded_by_reservation_id: number | null;
  superseded_at: string | null;
  superseded_reason: string | null;
  created_at: string | null;
}

export interface ReservationHistoryResponse {
  confirmation_code: string;
  customer_name: string;
  customer_email: string;
  total_versions: number;
  versions: ReservationVersion[];
}

// ============================================================================
// Check-In/Check-Out Types and Helpers
// ============================================================================

export interface CheckInEntry {
  time: string;
  notes?: string;
  site_id?: number;
}

export interface CheckOutEntry {
  time: string;
  notes?: string;
  site_id?: number;
}

export interface SpecialRequestsData {
  guest_requests?: string;
  check_ins?: CheckInEntry[];
  check_outs?: CheckOutEntry[];
  flagged?: boolean;  // Shows star badge on grid when true
  payment_type?: string; // 'cash' | 'check' | 'card_terminal' | etc — written at reservation creation
}

// Helper to parse special_requests (handles legacy text and JSON)
export function parseSpecialRequests(specialRequests?: string): SpecialRequestsData {
  if (!specialRequests) return {};
  try {
    const data = JSON.parse(specialRequests);
    return typeof data === 'object' ? data : { guest_requests: specialRequests };
  } catch {
    return { guest_requests: specialRequests };
  }
}

// Check-in: Adds a new check-in entry to the array (does NOT change reservation status)
export async function checkInReservation(
  reservationId: number,
  currentSpecialRequests: string | undefined,
  notes?: string,
  flagged?: boolean,
  siteId?: number
): Promise<any> {
  const data = parseSpecialRequests(currentSpecialRequests);

  // Initialize array if not exists
  if (!data.check_ins) data.check_ins = [];

  // Add new check-in entry; include site_id for per-site tracking when provided
  data.check_ins.push({
    time: getLocalDateTimeString(),
    notes: notes || undefined,
    ...(siteId != null ? { site_id: siteId } : {}),
  });

  // Update flagged status if provided
  if (flagged !== undefined) {
    data.flagged = flagged;
  }

  // Only update special_requests, NOT status
  return fetchWithAuth(`${API_BASE_URL}/reservations/${reservationId}`, {
    method: 'PUT',
    body: JSON.stringify({
      special_requests: JSON.stringify(data)
    }),
  });
}

// Check-out: Adds a new check-out entry to the array (does NOT change reservation status)
export async function checkOutReservation(
  reservationId: number,
  currentSpecialRequests: string | undefined,
  notes?: string,
  flagged?: boolean,
  siteId?: number
): Promise<any> {
  const data = parseSpecialRequests(currentSpecialRequests);

  // Initialize array if not exists
  if (!data.check_outs) data.check_outs = [];

  // Add new check-out entry; include site_id for per-site tracking when provided
  data.check_outs.push({
    time: getLocalDateTimeString(),
    notes: notes || undefined,
    ...(siteId != null ? { site_id: siteId } : {}),
  });

  // Update flagged status if provided
  if (flagged !== undefined) {
    data.flagged = flagged;
  }

  // Only update special_requests, NOT status
  return fetchWithAuth(`${API_BASE_URL}/reservations/${reservationId}`, {
    method: 'PUT',
    body: JSON.stringify({
      special_requests: JSON.stringify(data)
    }),
  });
}

// Remove a check-in entry by index (for undoing mistakes)
export async function undoCheckIn(
  reservationId: number,
  currentSpecialRequests: string | undefined,
  indexToRemove: number
): Promise<any> {
  const data = parseSpecialRequests(currentSpecialRequests);

  if (!data.check_ins || indexToRemove < 0 || indexToRemove >= data.check_ins.length) {
    throw new Error('Invalid check-in index');
  }

  // Remove the check-in at the specified index
  data.check_ins.splice(indexToRemove, 1);

  // Also remove the flagged status when undoing a check-in
  data.flagged = false;

  return fetchWithAuth(`${API_BASE_URL}/reservations/${reservationId}`, {
    method: 'PUT',
    body: JSON.stringify({
      special_requests: JSON.stringify(data)
    }),
  });
}

// ============================================================================
// API Functions
// ============================================================================

export interface GetReservationsParams {
  storeId?: number;
  checkInFrom?: string;  // YYYY-MM-DD
  checkInTo?: string;    // YYYY-MM-DD
  activeOnly?: boolean;
  status?: string;
}

export async function getReservations(params: GetReservationsParams = {}): Promise<ApiReservation[]> {
  const searchParams = new URLSearchParams();
  if (params.storeId) searchParams.append('store_id', params.storeId.toString());
  if (params.checkInFrom) searchParams.append('check_in_from', params.checkInFrom);
  if (params.checkInTo) searchParams.append('check_in_to', params.checkInTo);
  if (params.activeOnly !== undefined) searchParams.append('active_only', params.activeOnly.toString());
  if (params.status) searchParams.append('status', params.status);
  const queryString = searchParams.toString();
  return fetchWithAuth(`${API_BASE_URL}/reservations/${queryString ? '?' + queryString : ''}`);
}

export async function getReservationById(reservationId: number): Promise<ApiReservation> {
  return fetchWithAuth(`${API_BASE_URL}/reservations/${reservationId}`);
}

export async function getReservationModificationLogs(reservationId: number): Promise<ReservationModificationLog[]> {
  return fetchWithAuth(`${API_BASE_URL}/reservation-modification-logs/reservation/${reservationId}`);
}

export async function getReservationHistory(confirmationCode: string): Promise<ReservationHistoryResponse> {
  return fetchWithAuth(`${API_BASE_URL}/reservations/history?confirmation_code=${encodeURIComponent(confirmationCode)}`);
}

// ============================================================================
// Delete-related helpers: fetch reservations by member/customer, unlink FKs
// ============================================================================

export async function getReservationsByMemberId(memberId: number): Promise<ApiReservation[]> {
  return fetchWithAuth(`${API_BASE_URL}/reservations/?member_id=${memberId}&active_only=false`);
}

export async function getReservationsByCustomerId(customerId: number): Promise<ApiReservation[]> {
  return fetchWithAuth(`${API_BASE_URL}/reservations/?customer_id=${customerId}`);
}

export async function unlinkReservationMember(reservationId: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/reservations/${reservationId}`, {
    method: 'PUT',
    body: JSON.stringify({ member_id: null }),
  });
}

export async function unlinkReservationCustomer(reservationId: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/reservations/${reservationId}`, {
    method: 'PUT',
    body: JSON.stringify({ customer_id: null }),
  });
}

export async function resendBookingConfirmation(
  confirmationCode: string,
  overrideEmail?: string,
  includePdf: boolean = true
): Promise<any> {
  const body: Record<string, any> = {
    confirmation_code: confirmationCode,
    include_pdf: includePdf,
  };
  if (overrideEmail) {
    body.override_email = overrideEmail;
  }
  return fetchWithAuth(`${API_BASE_URL}/reservations/resend-booking-confirmation`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ============================================================================
// Payment-link hold expiry
// ============================================================================

// Must match PAYMENT_LINK_EXPIRATION_HOURS on the hcp-backend Cloud Run
// service (48h). The schedule feed doesn't include reservation.expires_at,
// so we derive it: hcp-backend sets expires_at = created_at + this window.
const PAYMENT_LINK_EXPIRATION_HOURS =
  Number(process.env.REACT_APP_PAYMENT_LINK_EXPIRATION_HOURS) || 48;

/**
 * True when a payment-link hold's window has lapsed without payment. The
 * backend's availability check stops blocking the site at that moment
 * (expires_at > now), so the schedule should stop displaying the hold too.
 * Unknown/unparseable created_at keeps the hold visible (safe default).
 */
export function isExpiredPaymentLinkHold(res: Reservation): boolean {
  if (res.status !== 'pending_payment' || !res.hasPaymentLink || !res.createdAt) {
    return false;
  }
  const created = parseBackendUtc(res.createdAt).getTime();
  if (isNaN(created)) return false;
  return Date.now() > created + PAYMENT_LINK_EXPIRATION_HOURS * 60 * 60 * 1000;
}

// ============================================================================
// Mapper: API Response → Grid Reservation[]
// ============================================================================

/**
 * Maps API reservations to the grid's Reservation[] format.
 * Multi-site reservations are expanded: one API reservation with N active
 * reservation_sites becomes N grid entries (one per site row).
 */
export function mapApiToGridReservations(apiReservations: ApiReservation[]): Reservation[] {
  // Filter out superseded reservation versions — the backend's active_only flag
  // doesn't exclude old versions, so both current and superseded can be returned.
  const currentOnly = apiReservations.filter(r => r.is_current_version !== false);
  const result: Reservation[] = [];

  for (const res of currentOnly) {
    const activeSites = (res.reservation_sites || []).filter(rs => rs.status === 'active');
    const checkIn = res.check_in.split('T')[0];
    const checkOut = addDays(checkIn, res.days);

    const guestName =
      `${res.customer?.first_name || ''} ${res.customer?.last_name || ''}`.trim() ||
      `${res.guest_first_name || ''} ${res.guest_last_name || ''}`.trim() ||
      'Guest';

    const guestEmail = res.customer?.email || '';
    const guestPhone = res.customer?.main_phone || '';

    // Admin bookings record their payment method in special_requests JSON at
    // creation. payment_link bookings hold the site as pending_payment and are
    // shown on the schedule; failed-MOTO holds (card_moto) stay hidden.
    const hasPaymentLink = parseSpecialRequests(res.special_requests).payment_type === 'payment_link';

    if (activeSites.length === 0) {
      // Reservation with no active sites — still show it with top-level occupancy
      result.push({
        id: res.id,
        confirmationCode: res.confirmation_code || '',
        memberId: res.member_id ?? null,
        siteId: 0,
        siteAlias: '',
        checkIn,
        checkOut,
        days: res.days,
        adults: res.adult,
        children: res.child,
        pets: res.pet,
        vehicles: res.vehicle,
        status: res.status as ReservationStatus,
        totalCost: res.cost,
        paymentStatus: 'pending' as PaymentStatus,
        paymentMethod: '',
        amountPaid: 0,
        createdAt: res.created_at || '',
        guestName,
        guestEmail,
        guestPhone,
        otherGuests: res.other_guests,
        specialRequests: res.special_requests,
        internalNotes: res.internal_notes,
        originalReservationId: res.original_reservation_id,
        isCurrentVersion: res.is_current_version,
        supersedesReservationId: res.supersedes_reservation_id,
        supersededByReservationId: res.superseded_by_reservation_id,
        hasPaymentLink,
      });
      continue;
    }

    for (const rs of activeSites) {
      result.push({
        id: res.id,
        confirmationCode: res.confirmation_code || '',
        memberId: res.member_id ?? null,
        siteId: rs.site_id,
        siteAlias: rs.site?.alias || '',
        checkIn,
        checkOut,
        days: res.days,
        adults: rs.adults,
        children: rs.children,
        pets: rs.pets,
        vehicles: rs.vehicles,
        status: res.status as ReservationStatus,
        totalCost: res.cost,
        paymentStatus: 'pending' as PaymentStatus,
        paymentMethod: '',
        amountPaid: 0,
        createdAt: res.created_at || '',
        guestName,
        guestEmail,
        guestPhone,
        otherGuests: res.other_guests,
        specialRequests: res.special_requests,
        internalNotes: res.internal_notes,
        originalReservationId: res.original_reservation_id,
        isCurrentVersion: res.is_current_version,
        supersedesReservationId: res.supersedes_reservation_id,
        supersededByReservationId: res.superseded_by_reservation_id,
        bookedRv: rs.rv ?? null,
        bookedPv: rs.pv ?? null,
        hasPaymentLink,
      });
    }
  }

  return result;
}

export function attachRvInfoToReservations(
  reservations: Reservation[],
  rvMap: Map<number, ReservationRvInfo[]>
): Reservation[] {
  return reservations.map(r => {
    if (r.memberId != null && rvMap.has(r.memberId)) {
      return { ...r, rvInfo: rvMap.get(r.memberId) };
    }
    return r;
  });
}
